import { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Users } from 'lucide-react';

interface ClientCalendarProps {
  interventions: any[];
}

export default function ClientCalendar({ interventions }: ClientCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month'); // For client, month is a better default

  // Helpers
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  
  const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const addMonths = (date: Date, months: number) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
  };

  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else {
      setCurrentDate(addDays(currentDate, -7));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 7));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const renderDayInterventions = (dayDate: Date) => {
    const dayInterventions = interventions.filter(i => {
      const iDate = new Date(i.scheduled_at);
      return isSameDay(iDate, dayDate);
    });

    dayInterventions.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());

    return dayInterventions.map(i => {
      const date = new Date(i.scheduled_at);
      const isPast = date < new Date();
      
      return (
        <div key={i.id} className={`p-1.5 mb-1 text-[10px] rounded border-l-2 ${isPast ? 'bg-gray-50 border-gray-300 opacity-75' : 'bg-gold-50 border-gold-500 shadow-sm'}`}>
          <div className="font-bold flex items-center gap-1 text-black-deep">
            <Clock size={10} className="text-gold-600"/>
            {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} ({i.duration_hours}h)
          </div>
          {i.collaborator ? (
            <div className="text-gray-600 truncate flex items-center gap-1 mt-0.5 font-medium">
              <Users size={10}/> {i.collaborator.full_name}
            </div>
          ) : (
            <div className="text-gray-500 font-medium truncate mt-0.5 italic">Intervenant à confirmer</div>
          )}
        </div>
      );
    });
  };

  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    let firstDay = getFirstDayOfMonth(year, month);
    if (firstDay === 0) firstDay = 7;
    
    const days = [];
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    const headers = weekDays.map(day => (
      <div key={day} className="text-center font-bold text-xs uppercase tracking-widest text-gray-500 py-3 border-b border-gray-100 bg-white">
        {day}
      </div>
    ));

    for (let i = 1; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="bg-gray-50/30 min-h-[100px] border-b border-r border-gray-100"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isToday = isSameDay(date, new Date());
      
      days.push(
        <div key={i} className={`min-h-[120px] p-1 border-b border-r border-gray-100 flex flex-col transition-colors ${isToday ? 'bg-gold-50/20' : 'bg-white hover:bg-gray-50/50'}`}>
          <div className="text-right p-1">
            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded-full ${isToday ? 'bg-gold-500 text-white font-bold shadow-sm' : 'text-gray-500 font-medium'}`}>
              {i}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col gap-1 p-1">
            {renderDayInterventions(date)}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-gray-100">
          {headers}
        </div>
        <div className="grid grid-cols-7 bg-gray-50">
          {days}
        </div>
      </div>
    );
  };

  const renderWeek = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    const headers = weekDays.map((dayName, idx) => {
      const date = addDays(startOfWeek, idx);
      const isToday = isSameDay(date, new Date());
      return (
        <div key={idx} className={`text-center py-4 border-b border-gray-100 ${isToday ? 'bg-gold-50/30' : 'bg-white'}`}>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">{dayName}</div>
          <div className={`text-xl mt-1 ${isToday ? 'text-gold-600 font-bold' : 'text-black-deep'}`}>
            {date.getDate()}
          </div>
        </div>
      );
    });

    const columns = weekDays.map((_, idx) => {
      const date = addDays(startOfWeek, idx);
      const isToday = isSameDay(date, new Date());
      return (
        <div key={idx} className={`min-h-[400px] border-r border-gray-100 p-2 flex flex-col gap-2 ${isToday ? 'bg-gold-50/10' : 'bg-white'}`}>
          {renderDayInterventions(date)}
        </div>
      );
    });

    return (
      <div className="bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden flex flex-col">
        <div className="grid grid-cols-7">
          {headers}
        </div>
        <div className="grid grid-cols-7 flex-1">
          {columns}
        </div>
      </div>
    );
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const monthName = monthNames[currentDate.getMonth()];
  const year = currentDate.getFullYear();
  
  let headerTitle = '';
  if (viewMode === 'month') {
    headerTitle = `${monthName} ${year}`;
  } else {
    const start = getStartOfWeek(currentDate);
    const end = addDays(start, 6);
    if (start.getMonth() === end.getMonth()) {
       headerTitle = `${start.getDate()} - ${end.getDate()} ${monthNames[start.getMonth()]} ${year}`;
    } else {
       headerTitle = `${start.getDate()} ${monthNames[start.getMonth()]} - ${end.getDate()} ${monthNames[end.getMonth()]} ${year}`;
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Calendar Toolbar */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-lg shadow-xl border border-gold-200/50 gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto justify-between">
          <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-sm border border-gray-100">
            <button onClick={() => setViewMode('month')} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${viewMode === 'month' ? 'bg-white shadow text-black-deep border border-gray-200' : 'text-gray-500 hover:text-black-deep'}`}>Mois</button>
            <button onClick={() => setViewMode('week')} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${viewMode === 'week' ? 'bg-white shadow text-black-deep border border-gray-200' : 'text-gray-500 hover:text-black-deep'}`}>Semaine</button>
          </div>
          <button onClick={handleToday} className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-gold-600 hover:bg-gold-50 rounded-sm transition-colors border border-gold-200 hidden md:block">
            Aujourd'hui
          </button>
        </div>
        
        <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
          <button onClick={handlePrev} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"><ChevronLeft size={20}/></button>
          <h2 className="font-serif text-2xl w-48 text-center text-black-deep">{headerTitle}</h2>
          <button onClick={handleNext} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"><ChevronRight size={20}/></button>
        </div>
      </div>

      {/* Calendar Grid */}
      {viewMode === 'month' ? renderMonth() : renderWeek()}
      
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
